/**
 * Reading package photos (pure functions, no I/O): the Nutrition Facts panel, the front of the
 * pack and the ingredient list, one AI call per photo.
 *
 * The model only transcribes what is printed. Everything it returns is range-checked here,
 * converted to catalog keys (per serving as printed, and per 100 g when the serving weight is
 * printed) and run through the True Label analyzer, so the app can show whether the printed
 * calories agree with the printed macros before the user saves the food.
 */
import { maxPer100g, NUTRIENT_BY_KEY, roundNutrient, type NutrientMap, type NutrientSourceMap } from '../nutrition/catalog';
import { analyzeLabel, gramInterval, intervalMid, LESS_THAN_ONE, type DeclaredGrams, type DeclaredLabel, type TrueLabelReport } from '../tef';

// ---------------------------------------------------------------------
// Parts
// ---------------------------------------------------------------------
export type LabelPart = 'nutrition' | 'front' | 'ingredients';
export const LABEL_PARTS: readonly LabelPart[] = ['nutrition', 'front', 'ingredients'];

// ---------------------------------------------------------------------
// The printed lines we read, and where they go in the catalog
// ---------------------------------------------------------------------
export type LabelField =
  | 'calories' | 'total_fat_g' | 'saturated_fat_g' | 'trans_fat_g' | 'cholesterol_mg' | 'sodium_mg'
  | 'total_carbohydrate_g' | 'dietary_fiber_g' | 'total_sugars_g' | 'added_sugars_g' | 'sugar_alcohol_g'
  | 'protein_g' | 'vitamin_d_mcg' | 'calcium_mg' | 'iron_mg' | 'potassium_mg';

interface FieldDef {
  field: LabelField;
  /** Catalog key. */
  key: string;
  /** Multiplier from the printed unit to the catalog unit (vitamin D: mcg to IU). */
  factor: number;
  /** Largest plausible amount for one serving, in the printed unit. */
  max: number;
  /** FDA allows "<1 g" on this line (21 CFR 101.9(c)). */
  lessThanOneAllowed: boolean;
  description: string;
}

export const LABEL_FIELD_DEFS: readonly FieldDef[] = [
  { field: 'calories', key: 'energy_kcal', factor: 1, max: 5000, lessThanOneAllowed: false, description: 'Calories for one serving.' },
  { field: 'total_fat_g', key: 'fat_g', factor: 1, max: 500, lessThanOneAllowed: false, description: 'Total Fat, grams.' },
  { field: 'saturated_fat_g', key: 'saturated_g', factor: 1, max: 500, lessThanOneAllowed: false, description: 'Saturated Fat, grams.' },
  { field: 'trans_fat_g', key: 'trans_g', factor: 1, max: 500, lessThanOneAllowed: false, description: 'Trans Fat, grams.' },
  { field: 'cholesterol_mg', key: 'cholesterol_mg', factor: 1, max: 5000, lessThanOneAllowed: false, description: 'Cholesterol, milligrams.' },
  { field: 'sodium_mg', key: 'sodium_mg', factor: 1, max: 50_000, lessThanOneAllowed: false, description: 'Sodium, milligrams.' },
  { field: 'total_carbohydrate_g', key: 'carbs_g', factor: 1, max: 1000, lessThanOneAllowed: true, description: 'Total Carbohydrate, grams.' },
  { field: 'dietary_fiber_g', key: 'fiber_g', factor: 1, max: 500, lessThanOneAllowed: true, description: 'Dietary Fiber, grams.' },
  { field: 'total_sugars_g', key: 'sugars_g', factor: 1, max: 1000, lessThanOneAllowed: true, description: 'Total Sugars, grams.' },
  { field: 'added_sugars_g', key: 'added_sugars_g', factor: 1, max: 1000, lessThanOneAllowed: true, description: 'Includes ...g Added Sugars, grams.' },
  { field: 'sugar_alcohol_g', key: 'sugar_alcohols_g', factor: 1, max: 500, lessThanOneAllowed: true, description: 'Sugar Alcohol, grams (often not printed).' },
  { field: 'protein_g', key: 'protein_g', factor: 1, max: 500, lessThanOneAllowed: true, description: 'Protein, grams.' },
  { field: 'vitamin_d_mcg', key: 'vit_d_iu', factor: 40, max: 1000, lessThanOneAllowed: false, description: 'Vitamin D in mcg (the amount, not the % Daily Value).' },
  { field: 'calcium_mg', key: 'calcium_mg', factor: 1, max: 10_000, lessThanOneAllowed: false, description: 'Calcium in mg (the amount, not the % Daily Value).' },
  { field: 'iron_mg', key: 'iron_mg', factor: 1, max: 1000, lessThanOneAllowed: false, description: 'Iron in mg (the amount, not the % Daily Value).' },
  { field: 'potassium_mg', key: 'potassium_mg', factor: 1, max: 20_000, lessThanOneAllowed: false, description: 'Potassium in mg (the amount, not the % Daily Value).' },
];

const FIELD_BY_NAME: ReadonlyMap<LabelField, FieldDef> = new Map(LABEL_FIELD_DEFS.map((d) => [d.field, d]));

// ---------------------------------------------------------------------
// Prompts and response schemas (one per photo)
// ---------------------------------------------------------------------
const printed = (description: string): Record<string, unknown> => ({ type: 'number', description: `${description} -1 when this line is not printed.` });

export const NUTRITION_SYSTEM_INSTRUCTION = `You transcribe a US or Canadian Nutrition Facts panel from a photo, exactly as printed.

Rules:
- Read the "per serving" column. If there is also a "per container" column, ignore it.
- serving_size_text is the serving line as printed, e.g. "2/3 cup (55g)" or "1 bar (60 g)".
- serving_size_g is the gram (or mL for drinks) number in that line. -1 if no weight is printed.
- servings_per_container: the number printed ("about 8" is 8). -1 if not printed.
- Every other field is the number printed for ONE serving, in the unit the field names. -1 when the line is not on the label.
- A line printed as "<1g", "less than 1g" or "less than 1 gram": put 0.5 and add the field name to less_than_one.
- Vitamin D, calcium, iron and potassium: use the amount (mcg or mg), never the % Daily Value. If only a % is printed, use -1.
- Never calculate, estimate or guess a number that is not printed. Never use a % Daily Value as an amount.
- is_nutrition_label is false when the photo does not show a Nutrition Facts panel or the numbers cannot be read.
- unreadable lists the fields you could not read clearly (glare, blur, cut off).`;

export const NUTRITION_RESPONSE_JSON_SCHEMA: Record<string, unknown> = {
  type: 'object',
  properties: {
    is_nutrition_label: { type: 'boolean' },
    serving_size_text: { type: 'string', description: 'Serving line as printed, or an empty string.' },
    serving_size_g: printed('Serving weight in grams (mL for drinks).'),
    servings_per_container: printed('Servings per container.'),
    ...Object.fromEntries(LABEL_FIELD_DEFS.map((d) => [d.field, printed(d.description)])),
    less_than_one: { type: 'array', items: { type: 'string' }, description: 'Field names printed as "<1 g".' },
    unreadable: { type: 'array', items: { type: 'string' }, description: 'Field names that could not be read clearly.' },
  },
  required: ['is_nutrition_label', 'serving_size_text', 'serving_size_g', 'servings_per_container', ...LABEL_FIELD_DEFS.map((d) => d.field), 'less_than_one', 'unreadable'],
};

export const FRONT_SYSTEM_INSTRUCTION = `You read the front of a food package from a photo.

Rules:
- product_name: the product name as printed, in normal capitalisation, without the brand and without marketing claims ("Chocolate Peanut Butter Protein Bar", not "NEW! 20g PROTEIN").
- brand: the brand or maker as printed. Empty string if none is visible.
- net_quantity: the net weight or volume line as printed, e.g. "NET WT 12 OZ (340g)". Empty string if not visible.
- is_food_package is false when the photo is not a food or drink package.
- Never invent a brand or a name that is not printed.`;

export const FRONT_RESPONSE_JSON_SCHEMA: Record<string, unknown> = {
  type: 'object',
  properties: {
    is_food_package: { type: 'boolean' },
    product_name: { type: 'string' },
    brand: { type: 'string' },
    net_quantity: { type: 'string' },
  },
  required: ['is_food_package', 'product_name', 'brand', 'net_quantity'],
};

export const INGREDIENTS_SYSTEM_INSTRUCTION = `You transcribe the ingredient list of a food package from a photo, exactly as printed.

Rules:
- ingredients: the full list after "Ingredients:", in normal capitalisation, keeping parentheses and commas. Empty string if not visible.
- allergens: the items in a "Contains:" statement (for example "milk", "soy", "peanuts"), lowercase. Empty array if there is no such statement.
- is_ingredient_list is false when the photo does not show an ingredient list or it cannot be read.
- Never add ingredients or allergens that are not printed.`;

export const INGREDIENTS_RESPONSE_JSON_SCHEMA: Record<string, unknown> = {
  type: 'object',
  properties: {
    is_ingredient_list: { type: 'boolean' },
    ingredients: { type: 'string' },
    allergens: { type: 'array', items: { type: 'string' } },
  },
  required: ['is_ingredient_list', 'ingredients', 'allergens'],
};

export const PART_PROMPTS: Readonly<Record<LabelPart, { system: string; schema: Record<string, unknown>; user: string; maxTokens: number }>> = {
  nutrition: { system: NUTRITION_SYSTEM_INSTRUCTION, schema: NUTRITION_RESPONSE_JSON_SCHEMA, user: 'Transcribe this Nutrition Facts panel.', maxTokens: 900 },
  front: { system: FRONT_SYSTEM_INSTRUCTION, schema: FRONT_RESPONSE_JSON_SCHEMA, user: 'Read the name, brand and net quantity on this package.', maxTokens: 300 },
  ingredients: { system: INGREDIENTS_SYSTEM_INSTRUCTION, schema: INGREDIENTS_RESPONSE_JSON_SCHEMA, user: 'Transcribe this ingredient list.', maxTokens: 1500 },
};

// ---------------------------------------------------------------------
// Result
// ---------------------------------------------------------------------
export type PartStatus = 'ok' | 'unreadable' | 'failed' | 'skipped';

export interface ReadLabelResult {
  product: { name: string | null; brand: string | null; netQuantity: string | null };
  label: {
    found: boolean;
    servingSizeText: string | null;
    servingSizeG: number | null;
    servingsPerContainer: number | null;
    /** As printed for one serving. A "<1 g" line holds 0.5 and is listed in lessThanOne. */
    perServing: Partial<Record<LabelField, number>>;
    lessThanOne: LabelField[];
    unreadable: string[];
  };
  /** Catalog keys for one serving as printed. */
  perServing: NutrientMap;
  /** Catalog keys per 100 g; empty when the label prints no serving weight. */
  per100g: NutrientMap;
  sources: NutrientSourceMap;
  ingredients: string | null;
  allergens: string[];
  labelCheck: { declared: DeclaredLabel; report: TrueLabelReport } | null;
  labelCheckSkipped: string | null;
  warnings: string[];
  parts: Record<LabelPart, PartStatus>;
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)) ? Number(v) : null);
const text = (v: unknown, max: number): string | null => {
  if (typeof v !== 'string') return null;
  const t: string = v.replace(/\s+/g, ' ').trim();
  return t.length > 0 ? t.slice(0, max) : null;
};
const LESS_THAN_ONE_MID: number = intervalMid(gramInterval(LESS_THAN_ONE));

/** The model's answers for the photos it read, merged into one result. Missing parts are simply absent. */
export function normalizeLabelRead(answers: Partial<Record<LabelPart, unknown>>, parts: Record<LabelPart, PartStatus>): ReadLabelResult {
  const warnings: string[] = [];
  const statuses: Record<LabelPart, PartStatus> = { ...parts };

  // ---- front ----
  let product: ReadLabelResult['product'] = { name: null, brand: null, netQuantity: null };
  if (answers.front !== undefined) {
    const f = (answers.front ?? {}) as Record<string, unknown>;
    if (f.is_food_package === false) {
      statuses.front = 'unreadable';
      warnings.push('The front photo did not look like a food package, so the name and brand were not read.');
    } else {
      product = { name: text(f.product_name, 300), brand: text(f.brand, 120), netQuantity: text(f.net_quantity, 120) };
    }
  }

  // ---- ingredients ----
  let ingredients: string | null = null;
  let allergens: string[] = [];
  if (answers.ingredients !== undefined) {
    const g = (answers.ingredients ?? {}) as Record<string, unknown>;
    if (g.is_ingredient_list === false) {
      statuses.ingredients = 'unreadable';
      warnings.push('The ingredients photo could not be read. Try again closer, with less glare.');
    } else {
      ingredients = text(g.ingredients, 4000);
      allergens = Array.isArray(g.allergens)
        ? [...new Set(g.allergens.map((a) => text(a, 40)?.toLowerCase()).filter((a): a is string => a !== null && a !== undefined))].slice(0, 40)
        : [];
    }
  }

  // ---- nutrition ----
  const label: ReadLabelResult['label'] = { found: false, servingSizeText: null, servingSizeG: null, servingsPerContainer: null, perServing: {}, lessThanOne: [], unreadable: [] };
  const perServing: NutrientMap = {};
  const per100g: NutrientMap = {};
  let labelCheck: ReadLabelResult['labelCheck'] = null;
  let labelCheckSkipped: string | null = 'No Nutrition Facts photo was read.';

  if (answers.nutrition !== undefined) {
    const n = (answers.nutrition ?? {}) as Record<string, unknown>;
    if (n.is_nutrition_label === false) {
      statuses.nutrition = 'unreadable';
      warnings.push('The Nutrition Facts photo could not be read. Try again straight on, filling the frame, without glare.');
      labelCheckSkipped = 'The Nutrition Facts panel could not be read.';
    } else {
      label.found = true;
      label.servingSizeText = text(n.serving_size_text, 120);
      const sg: number | null = num(n.serving_size_g);
      label.servingSizeG = sg !== null && sg > 0 && sg <= 5000 ? sg : null;
      const spc: number | null = num(n.servings_per_container);
      label.servingsPerContainer = spc !== null && spc > 0 && spc <= 1000 ? spc : null;
      const ltOne: Set<string> = new Set(Array.isArray(n.less_than_one) ? n.less_than_one.filter((x): x is string => typeof x === 'string') : []);
      label.unreadable = Array.isArray(n.unreadable) ? n.unreadable.filter((x): x is string => typeof x === 'string').slice(0, 20) : [];

      for (const def of LABEL_FIELD_DEFS) {
        const raw: number | null = num(n[def.field]);
        if (ltOne.has(def.field) && def.lessThanOneAllowed) {
          label.perServing[def.field] = LESS_THAN_ONE_MID;
          label.lessThanOne.push(def.field);
          continue;
        }
        if (raw === null || raw < 0) continue; // not printed
        if (raw > def.max) {
          warnings.push(`Ignored ${NUTRIENT_BY_KEY.get(def.key)?.name ?? def.field}: ${raw} per serving is not plausible, so it was probably misread.`);
          continue;
        }
        label.perServing[def.field] = raw;
      }
      consistencyWarnings(label.perServing, warnings);

      for (const [field, v] of Object.entries(label.perServing) as Array<[LabelField, number]>) {
        const def: FieldDef = FIELD_BY_NAME.get(field)!;
        perServing[def.key] = roundNutrient(v * def.factor);
      }
      if (label.servingSizeG !== null) {
        for (const [key, v] of Object.entries(perServing)) {
          const per: number = (v * 100) / label.servingSizeG;
          const cat = NUTRIENT_BY_KEY.get(key);
          if (cat && per > maxPer100g(cat)) {
            warnings.push(`The serving weight (${label.servingSizeG} g) looks misread: ${cat.name} would be ${roundNutrient(per)} ${cat.unit} per 100 g. Check the serving size.`);
            for (const k of Object.keys(per100g)) delete per100g[k];
            break;
          }
          per100g[key] = roundNutrient(per);
        }
      } else if (Object.keys(perServing).length > 0) {
        warnings.push('No serving weight in grams was printed, so the values are kept per serving. Enter the weight to log by grams.');
      }

      const declared: DeclaredLabel | string = declaredFrom(label);
      if (typeof declared === 'string') labelCheckSkipped = declared;
      else {
        labelCheck = { declared, report: analyzeLabel(declared) };
        labelCheckSkipped = null;
      }
      if (label.unreadable.length > 0) warnings.push(`Double-check these lines, the photo was unclear: ${label.unreadable.join(', ')}.`);
    }
  }

  const sources: NutrientSourceMap = Object.fromEntries(Object.keys(perServing).map((k) => [k, 'label'])) as NutrientSourceMap;
  return { product, label, perServing, per100g, sources, ingredients, allergens, labelCheck, labelCheckSkipped, warnings, parts: statuses };
}

function consistencyWarnings(p: Partial<Record<LabelField, number>>, warnings: string[]): void {
  const over = (part: LabelField, whole: LabelField, what: string): void => {
    const a: number | undefined = p[part];
    const b: number | undefined = p[whole];
    if (a !== undefined && b !== undefined && a > b + 0.5) warnings.push(`${what} is more than the total it belongs to, so one of them was probably misread.`);
  };
  over('dietary_fiber_g', 'total_carbohydrate_g', 'Fiber');
  over('total_sugars_g', 'total_carbohydrate_g', 'Sugars');
  over('added_sugars_g', 'total_sugars_g', 'Added sugars');
  over('saturated_fat_g', 'total_fat_g', 'Saturated fat');
}

const declaredGrams = (v: number | undefined, lessThanOne: boolean): DeclaredGrams | null =>
  lessThanOne ? LESS_THAN_ONE : v === undefined ? null : v;

/** The panel as the True Label analyzer takes it, or why it can't run. */
export function declaredFrom(label: ReadLabelResult['label']): DeclaredLabel | string {
  const p = label.perServing;
  const lt = (f: LabelField): boolean => label.lessThanOne.includes(f);
  const carbs: DeclaredGrams | null = declaredGrams(p.total_carbohydrate_g, lt('total_carbohydrate_g'));
  const protein: DeclaredGrams | null = declaredGrams(p.protein_g, lt('protein_g'));
  if (p.calories === undefined || p.total_fat_g === undefined || carbs === null || protein === null) {
    return 'The label check needs calories, total fat, total carbohydrate and protein.';
  }
  return {
    caloriesKcal: p.calories,
    fatG: p.total_fat_g,
    totalCarbG: carbs,
    proteinG: protein,
    fiberG: declaredGrams(p.dietary_fiber_g, lt('dietary_fiber_g')),
    sugarAlcoholG: declaredGrams(p.sugar_alcohol_g, lt('sugar_alcohol_g')),
    servingSizeG: label.servingSizeG,
    servingsPerContainer: label.servingsPerContainer,
  };
}
